import { getZoomOptions, getApertureOptions, getIrLightOptions, getIrCutFilterOptions } from '@/api/controlUnitApi'
import { Client } from '@/api/controlUnitApi/client'
import { putZoomAbsolute, putApertureAbsolute, putIrLightAbsolute, putIrCutFilter } from '@/api/controlUnitApi/sdk.gen'
import { CameraPosition, IrLightPosition } from '@/api/controlUnitApi/types.gen'
import { notifications } from '@mantine/notifications'
import { QueryKey, useMutation, useQueries, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { HARDWARE_OTHER_PANEL_REFETCH_INTERVAL_MS } from '@/constants'
import { extendKey } from '@/utils/queryKey'

const refetchInterval = HARDWARE_OTHER_PANEL_REFETCH_INTERVAL_MS

const cameraLabelKey = (position: CameraPosition) =>
  position === 'FL' ? 'frontLeft' : position === 'FC' ? 'frontCenter' : 'frontRight'

const irLabelKey = (position: IrLightPosition) => (position === 'FL' ? 'frontLeft' : 'frontRight')

function useOtherPanelData({
  client,
  source,
  isRefetchingCombinedView,
}: {
  client: Client
  source: string
  isRefetchingCombinedView: boolean
}) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('hardware')

  const flZoomOpts = extendKey(getZoomOptions({ path: { camera_position: 'FL' }, client }), source)
  const frZoomOpts = extendKey(getZoomOptions({ path: { camera_position: 'FR' }, client }), source)
  const fcZoomOpts = extendKey(getZoomOptions({ path: { camera_position: 'FC' }, client }), source)

  const flApertureOpts = extendKey(getApertureOptions({ path: { camera_position: 'FL' }, client }), source)
  const frApertureOpts = extendKey(getApertureOptions({ path: { camera_position: 'FR' }, client }), source)
  const fcApertureOpts = extendKey(getApertureOptions({ path: { camera_position: 'FC' }, client }), source)

  const flIrOpts = extendKey(getIrLightOptions({ path: { ir_light_position: 'FL' }, client }), source)
  const frIrOpts = extendKey(getIrLightOptions({ path: { ir_light_position: 'FR' }, client }), source)

  const flIrCutFilterOpts = extendKey(getIrCutFilterOptions({ path: { camera_position: 'FL' }, client }), source)
  const frIrCutFilterOpts = extendKey(getIrCutFilterOptions({ path: { camera_position: 'FR' }, client }), source)
  const fcIrCutFilterOpts = extendKey(getIrCutFilterOptions({ path: { camera_position: 'FC' }, client }), source)

  const allQueries = useQueries({
    queries: [
      { ...flZoomOpts, refetchInterval },
      { ...frZoomOpts, refetchInterval },
      { ...fcZoomOpts, refetchInterval },
      { ...flApertureOpts },
      { ...frApertureOpts, refetchInterval },
      { ...fcApertureOpts, refetchInterval },
      { ...flIrOpts, refetchInterval },
      { ...frIrOpts, refetchInterval },
      { ...flIrCutFilterOpts, refetchInterval },
      { ...frIrCutFilterOpts, refetchInterval },
      { ...fcIrCutFilterOpts, refetchInterval },
    ],
  })

  const frontLeftZoomData = allQueries[0].data?.level
  const frontRightZoomData = allQueries[1].data?.level
  const frontCenterZoomData = allQueries[2].data?.level

  const frontLeftApertureData = allQueries[3].data?.level
  const frontRightApertureData = allQueries[4].data?.level
  const frontCenterApertureData = allQueries[5].data?.level

  const frontLeftIrData = allQueries[6].data?.level
  const frontRightIrData = allQueries[7].data?.level

  const frontLeftIrCutFilterData = allQueries[8].data?.enabled
  const frontRightIrCutFilterData = allQueries[9].data?.enabled
  const frontCenterIrCutFilterData = allQueries[10].data?.enabled

  const refetchFrontLeftZoom = allQueries[0].refetch
  const refetchFrontRightZoom = allQueries[1].refetch
  const refetchFrontCenterZoom = allQueries[2].refetch
  const refetchFrontLeftAperture = allQueries[3].refetch
  const refetchFrontRightAperture = allQueries[4].refetch
  const refetchFrontCenterAperture = allQueries[5].refetch
  const refetchFrontLeftIr = allQueries[6].refetch
  const refetchFrontRightIr = allQueries[7].refetch
  const refetchFrontLeftIrCutFilter = allQueries[8].refetch
  const refetchFrontRightIrCutFilter = allQueries[9].refetch
  const refetchFrontCenterIrCutFilter = allQueries[10].refetch

  const refetchZoomByPosition = (camera_position: CameraPosition) => {
    if (camera_position === 'FL') return refetchFrontLeftZoom()
    if (camera_position === 'FR') return refetchFrontRightZoom()
    return refetchFrontCenterZoom()
  }

  const refetchApertureByPosition = (camera_position: CameraPosition) => {
    if (camera_position === 'FL') return refetchFrontLeftAperture()
    if (camera_position === 'FR') return refetchFrontRightAperture()
    return refetchFrontCenterAperture()
  }

  const refetchIrByPosition = (ir_light_position: IrLightPosition) => {
    if (ir_light_position === 'FL') return refetchFrontLeftIr()
    return refetchFrontRightIr()
  }

  const refetchIrCutFilterByPosition = (camera_position: CameraPosition) => {
    if (camera_position === 'FL') return refetchFrontLeftIrCutFilter()
    if (camera_position === 'FR') return refetchFrontRightIrCutFilter()
    return refetchFrontCenterIrCutFilter()
  }

  const refetchAll = useCallback(
    () =>
      Promise.all([
        refetchFrontLeftZoom(),
        refetchFrontRightZoom(),
        refetchFrontCenterZoom(),
        refetchFrontLeftAperture(),
        refetchFrontRightAperture(),
        refetchFrontCenterAperture(),
        refetchFrontLeftIr(),
        refetchFrontRightIr(),
        refetchFrontLeftIrCutFilter(),
        refetchFrontRightIrCutFilter(),
        refetchFrontCenterIrCutFilter(),
      ]),
    [
      refetchFrontLeftZoom,
      refetchFrontRightZoom,
      refetchFrontCenterZoom,
      refetchFrontLeftAperture,
      refetchFrontRightAperture,
      refetchFrontCenterAperture,
      refetchFrontLeftIr,
      refetchFrontRightIr,
      refetchFrontLeftIrCutFilter,
      refetchFrontRightIrCutFilter,
      refetchFrontCenterIrCutFilter,
    ]
  )

  useEffect(() => {
    if (isRefetchingCombinedView) {
      refetchAll()
    }
  }, [isRefetchingCombinedView, refetchAll])

  const useZoomMutation = (camera_position: CameraPosition, queryKey: QueryKey) =>
    useMutation({
      mutationFn: (value: number) => putZoomAbsolute({ body: { level: value }, path: { camera_position }, client }),
      onMutate: value => {
        queryClient.setQueryData(queryKey, { camera_position, level: value })
      },
      onSuccess: () => {
        refetchZoomByPosition(camera_position)
        notifications.show({
          title: t('zoomValue'),
          message: t('zoomValueSet', { camera: t(cameraLabelKey(camera_position)) }),
          color: 'green',
        })
      },
    })

  const useApertureMutation = (camera_position: CameraPosition, queryKey: QueryKey) =>
    useMutation({
      mutationFn: (value: number) => putApertureAbsolute({ body: { level: value }, path: { camera_position }, client }),
      onMutate: value => {
        queryClient.setQueryData(queryKey, { camera_position, level: value })
      },
      onSuccess: () => {
        refetchApertureByPosition(camera_position)
        notifications.show({
          title: t('apertureValue'),
          message: t('apertureValueSet', { camera: t(cameraLabelKey(camera_position)) }),
          color: 'green',
        })
      },
    })

  const useIrMutation = (ir_light_position: IrLightPosition, queryKey: QueryKey) =>
    useMutation({
      mutationFn: (value: number) =>
        putIrLightAbsolute({ body: { level: value }, path: { ir_light_position }, client }),
      onMutate: value => {
        queryClient.setQueryData(queryKey, { ir_light_position, level: value })
      },
      onSuccess: () => {
        refetchIrByPosition(ir_light_position)
        notifications.show({
          title: t('infraredValue'),
          message: t('infraredValueSet', { camera: t(irLabelKey(ir_light_position)) }),
          color: 'green',
        })
      },
    })

  const useIrCutFilterMutation = (camera_position: CameraPosition, queryKey: QueryKey) =>
    useMutation({
      mutationFn: (value: boolean) => putIrCutFilter({ body: { enabled: value }, path: { camera_position }, client }),
      onMutate: value => {
        queryClient.setQueryData(queryKey, { camera_position, enabled: value })
      },
      onSuccess: () => {
        refetchIrCutFilterByPosition(camera_position)
        notifications.show({
          title: t('irCutFilter'),
          message: t('irCutFilterSet', { camera: t(cameraLabelKey(camera_position)) }),
          color: 'green',
        })
      },
    })

  const { mutate: mutateFrontLeftZoom } = useZoomMutation('FL', flZoomOpts.queryKey)
  const { mutate: mutateFrontRightZoom } = useZoomMutation('FR', frZoomOpts.queryKey)
  const { mutate: mutateFrontCenterZoom } = useZoomMutation('FC', fcZoomOpts.queryKey)

  const { mutate: mutateFrontLeftAperture } = useApertureMutation('FL', flApertureOpts.queryKey)
  const { mutate: mutateFrontRightAperture } = useApertureMutation('FR', frApertureOpts.queryKey)
  const { mutate: mutateFrontCenterAperture } = useApertureMutation('FC', fcApertureOpts.queryKey)

  const { mutate: mutateFrontLeftIr } = useIrMutation('FL', flIrOpts.queryKey)
  const { mutate: mutateFrontRightIr } = useIrMutation('FR', frIrOpts.queryKey)

  const { mutate: mutateFrontLeftIrCutFilter } = useIrCutFilterMutation('FL', flIrCutFilterOpts.queryKey)
  const { mutate: mutateFrontRightIrCutFilter } = useIrCutFilterMutation('FR', frIrCutFilterOpts.queryKey)
  const { mutate: mutateFrontCenterIrCutFilter } = useIrCutFilterMutation('FC', fcIrCutFilterOpts.queryKey)

  const isRefetching = useMemo(() => allQueries.some(query => query.isRefetching), [allQueries])

  return {
    frontLeftZoomData,
    frontRightZoomData,
    frontCenterZoomData,
    mutateFrontLeftZoom,
    mutateFrontRightZoom,
    mutateFrontCenterZoom,
    frontLeftApertureData,
    frontRightApertureData,
    frontCenterApertureData,
    mutateFrontLeftAperture,
    mutateFrontRightAperture,
    mutateFrontCenterAperture,
    frontLeftIrData,
    frontRightIrData,
    mutateFrontLeftIr,
    mutateFrontRightIr,
    frontLeftIrCutFilterData,
    frontRightIrCutFilterData,
    frontCenterIrCutFilterData,
    mutateFrontLeftIrCutFilter,
    mutateFrontRightIrCutFilter,
    mutateFrontCenterIrCutFilter,
    isRefetching,
  }
}

export default useOtherPanelData
